import { create } from "zustand";
import marketdata from "../data/Services/marketdata";
import { isFeatureEnabled } from "../data/Services/settings";
import logger from "../utils/logger";

/**
 * Rating Store - Manages app rating modal state and server persistence
 *
 * Features:
 * - Tracks if rating modal should be shown
 * - Persists user's rating history to server via marketdata storage API
 * - Keeps all ratings with timestamps to track impression changes over time
 * - Prevents auto-show if user has already rated (but allows manual re-rating)
 * - DEV_MODE: Always shows modal on chart load for testing
 */

// Development flag - set to false for production
const DEV_MODE = false;

// Rating version - bump manually to trigger a new rating round for all users
// Uses same format as package.json version, but set independently so minor
// versions can be skipped. Old ratings are preserved in history.
const RATING_VERSION = "1.4.5";

// Engagement trigger: N significant user actions (change symbol, add indicator, arm
// a drawing tool) arm the rating flow. Replaces the old charts[i].data proxy (dead
// since cef0108). Repetitions of the same action count.
const ENGAGEMENT_THRESHOLD = 3;
// Delay after the threshold before showing the modal. Engagement already proved the
// app works, so it's far shorter than the old 5-10 min wait. Dev: 2s for testing.
const ENGAGEMENT_DELAY_MS = 90 * 1000; // 90 seconds
const DEV_DELAY_MS = 2000; // 2 seconds for dev testing

const getEngagementDelay = () =>
  DEV_MODE ? DEV_DELAY_MS : ENGAGEMENT_DELAY_MS;

/**
 * Compara versões pontilhadas numericamente, segmento a segmento (NÃO lexicográfico:
 * "1.4.10" > "1.4.2"). Ausente/vazio (ratings v1 sem `version`) = mais baixo (0.0.0).
 * @returns {number} <0 se a<b, 0 se iguais, >0 se a>b
 */
export const compareVersions = (a, b) => {
  const pa = String(a ?? "").split(".");
  const pb = String(b ?? "").split(".");
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    const x = Number(pa[i]) || 0;
    const y = Number(pb[i]) || 0;
    if (x !== y) return x - y;
  }
  return 0;
};

const useRatingStore = create((set, get) => ({
  // State
  isVisible: false,
  currentRating: null, // Most recent rating
  currentComment: null, // Most recent comment
  lastRatedAt: null, // Most recent rating timestamp
  ratingHistory: [], // Array of all ratings [{rating, comment, timestamp, ...}]
  isLoading: false,
  hasCheckedServer: false,
  serverRatingLoaded: false,
  engagementCount: 0, // significant user actions this session (engagement trigger)
  hasArmed: false, // armed the rating flow this session — idempotent; replaces App's ratingTriggered ref

  // Actions

  /**
   * Show the rating modal
   */
  showModal: () => {
    set({ isVisible: true });
  },

  /**
   * Hide the rating modal
   */
  hideModal: () => {
    set({ isVisible: false });
  },

  /**
   * Load rating history from server to check if user has already rated
   */
  loadRatingFromServer: async () => {
    if (get().hasCheckedServer) return;

    set({ isLoading: true });
    try {
      const serverData = await marketdata.getRating();
      if (serverData) {
        console.log(
          "[RatingStore] Found existing rating data on server:",
          serverData,
        );

        // Handle both old format (single rating) and new format (history array)
        const history = serverData.history || [serverData];
        const latestRating = history[history.length - 1];

        set({
          currentRating: latestRating?.rating || null,
          currentComment: latestRating?.comment || null,
          lastRatedAt: latestRating?.timestamp || null,
          ratingHistory: history,
          serverRatingLoaded: true,
          hasCheckedServer: true,
          isLoading: false,
        });
      } else {
        console.log("[RatingStore] No rating found on server");
        set({ hasCheckedServer: true, isLoading: false });
      }
    } catch (error) {
      console.error("[RatingStore] Failed to load rating from server:", error);
      set({ hasCheckedServer: true, isLoading: false });
    }
  },

  /**
   * Submit a rating - appends to history on server
   * @param {{rating: number, comment: string, timestamp: string}} data
   */
  submitRating: async (data) => {
    console.log("[RatingStore] Submitting rating:", data);

    set({ isLoading: true });

    const newEntry = {
      rating: data.rating,
      comment: data.comment,
      timestamp: data.timestamp,
      version: RATING_VERSION,
      userAgent: navigator.userAgent,
      screenSize: `${window.screen.width}x${window.screen.height}`,
    };

    // Get existing history and append new entry
    const existingHistory = get().ratingHistory || [];
    const updatedHistory = [...existingHistory, newEntry];

    try {
      // Save complete history to server
      await marketdata.saveRating({
        history: updatedHistory,
        // Also include latest for easy access
        rating: data.rating,
        comment: data.comment,
        timestamp: data.timestamp,
      });

      set({
        currentRating: data.rating,
        currentComment: data.comment,
        lastRatedAt: data.timestamp,
        ratingHistory: updatedHistory,
        isVisible: false,
        isLoading: false,
        serverRatingLoaded: true,
      });

      console.log(
        `[RatingStore] Rating saved to server successfully (total ratings: ${updatedHistory.length})`,
      );
    } catch (error) {
      console.error("[RatingStore] Failed to save rating to server:", error);
      // Still update local state even if server fails
      set({
        currentRating: data.rating,
        currentComment: data.comment,
        lastRatedAt: data.timestamp,
        ratingHistory: updatedHistory,
        isVisible: false,
        isLoading: false,
      });
    }
  },

  /**
   * Check if rating modal should be shown automatically
   * @returns {boolean}
   */
  shouldShowRating: () => {
    // In dev mode, always allow showing
    if (DEV_MODE) {
      return true;
    }

    const { ratingHistory } = get();

    // Suprime se o usuário já avaliou a versão ARMADA ou uma MAIS NOVA (>=). Com
    // `===`, quem avaliou uma versão mais nova que a armada (rollout escalonado /
    // rollback / versão pulada) era re-perguntado indevidamente. Entries v1 (sem
    // `version`) = 0.0.0 → continuam elegíveis.
    const hasRatedThisOrLater = ratingHistory.some(
      (entry) => compareVersions(entry.version, RATING_VERSION) >= 0,
    );

    return !hasRatedThisOrLater;
  },

  /**
   * Register a significant user-engagement action (change symbol, add indicator,
   * arm a drawing tool). Called imperatively from chartStore actions — NEVER from
   * render/quote paths (I5). Replaces the old charts[i].data proxy (dead since
   * cef0108). Arms the rating flow once per session when the threshold is reached.
   */
  registerEngagement: () => {
    // Gate at USE time (never module scope): kill-switch via runtime-config.json.
    if (!isFeatureEnabled("ratingEngagementTrigger")) return;

    if (get().hasArmed) return; // idempotent per session

    const engagementCount = get().engagementCount + 1;
    set({ engagementCount });
    if (engagementCount < ENGAGEMENT_THRESHOLD) return;

    // Threshold reached: arm once (idempotent) and run the async flow. Make the
    // fire-and-forget policy explicit — a rejection here must never be silent (I2):
    // this whole feature exists because a trigger died silently for 6 months.
    set({ hasArmed: true });
    get()
      .armRatingAfterEngagement()
      .catch((e) =>
        logger.system.error(
          "[RatingStore] arm flow failed (rating not shown)",
          {
            error: e?.message || String(e),
          },
        ),
      );
  },

  /**
   * Arm the rating modal after the engagement threshold: server → gate → delay.
   * (Was triggerAfterChartLoad; delay is now ~90s — engagement already proved the
   * app works, so no need for the old 5-10 min wait.)
   */
  armRatingAfterEngagement: async () => {
    const {
      loadRatingFromServer,
      shouldShowRating,
      showModal,
      hasCheckedServer,
    } = get();

    // Route lifecycle logs through the central logger (SYSTEM) so a future silent
    // death of this trigger is DETECTABLE in the remote pipeline — not just the
    // user's browser console. This feature exists precisely because the old trigger
    // died unnoticed (cef0108).
    logger.system.info(
      "[RatingStore] Armed — evaluating rating modal after delay",
    );

    // Check server for existing rating (only once)
    if (!hasCheckedServer) {
      await loadRatingFromServer();
    }

    if (!shouldShowRating()) {
      logger.system.info(
        "[RatingStore] Suppressed pre-delay (already rated this or a later version)",
      );
      return;
    }

    setTimeout(() => {
      // Re-check the flag so a mid-flight kill-switch (runtime-config.json) cancels
      // an already-armed timer — otherwise the flag is only checked at register time.
      if (!isFeatureEnabled("ratingEngagementTrigger")) {
        logger.system.info("[RatingStore] Post-delay: suppressed (flag off)");
        return;
      }
      // Double-check after delay in case state changed.
      if (get().shouldShowRating()) {
        logger.system.info(
          "[RatingStore] Showing rating modal after engagement delay",
        );
        showModal();
      } else {
        logger.system.info(
          "[RatingStore] Post-delay: suppressed (state changed before fire)",
        );
      }
    }, getEngagementDelay());
  },

  /**
   * Get rating history summary
   * @returns {{totalRatings: number, averageRating: number, latestRating: object}}
   */
  getRatingSummary: () => {
    const { ratingHistory } = get();
    if (!ratingHistory || ratingHistory.length === 0) {
      return { totalRatings: 0, averageRating: null, latestRating: null };
    }

    const total = ratingHistory.length;
    const sum = ratingHistory.reduce(
      (acc, entry) => acc + (entry.rating || 0),
      0,
    );
    const average = sum / total;

    return {
      totalRatings: total,
      averageRating: Math.round(average * 10) / 10,
      latestRating: ratingHistory[total - 1],
    };
  },

  /**
   * Reset all rating data (for testing)
   */
  resetRatingData: () => {
    set({
      isVisible: false,
      currentRating: null,
      currentComment: null,
      lastRatedAt: null,
      ratingHistory: [],
      isLoading: false,
      hasCheckedServer: false,
      serverRatingLoaded: false,
      engagementCount: 0,
      hasArmed: false,
    });
    console.log("[RatingStore] Rating data reset");
  },
}));

export default useRatingStore;
